import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useDataSourceProfile from '@afosto/utils-react/dataSource/useDataSourceProfile';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import updateDataGridDefaultView from '@afosto/utils-react/datagrid/updateDataGridDefaultView';
import updateDataGridProfilePositions from '@afosto/utils-react/datagrid/updateDataGridProfilePositions';
import updateDataGridProfileView from '@afosto/utils-react/datagrid/updateDataGridProfileView';
import createDataGridProfileView from '@afosto/utils-react/datagrid/createDataGridProfileView';
import filterOptionsQuery from '@afosto/utils-react/datagrid/filterOptionsQuery';
import searchQuery from '@afosto/utils-react/datagrid/searchQuery';
import { DataGrid } from '../DataGrid';
import DataList from '../DataList';
import createDefaultView from '../DataSource/modules/views/utils';
import Hidden from '@material-ui/core/Hidden';
import Grid from '@material-ui/core/Grid';
import OrderCard, { OrderCardSkeleton } from '../OrderCard/OrderCard';
import {
  clientFormatter,
  contactFormatter,
  orderFormatter,
  orderProgressStatusFormatter,
  orderTagsFormatter,
  orderTypeFormatter,
  organisationFormatter,
} from './formatters';
import OrderTagsFilter from './filterFormatters/OrderTagsFilter';
import OrderTagsRenderer from './filterRenderers/OrderTagsRenderer';
import OrderTypeFilter from './filterFormatters/OrderTypeFilter';
import OrderTypeRenderer from './filterRenderers/OrderTypeRenderer';
import OrderProgressStatusFilter from './filterFormatters/OrderProgressStatusFilter';
import OrderProgressStatusRenderer from './filterRenderers/OrderProgressStatusRenderer';

const ORDERS_PROFILE_CODE = 'odr';
const ORDERS_PROFILE_MODEL = 'orders';
const ORDERS_PROFILE_PATH = 'odr.orders';
const ORDERS_PROFILE_NAMESPACE = 'default';

const OrdersContainer = props => <Grid container spacing={2} {...props} />;

export const getDefaultOrderProperties = intl => [
  {
    key: 'total',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.total`,
      defaultMessage: 'Total',
      description: 'The column name for column total',
    }),
    type: 'currency',
  },
  {
    key: 'contact_id',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.customer`,
      defaultMessage: 'Customer',
      description: 'The column name for column customer',
    }),
    isSortable: false,
    type: 'contact',
  },
  {
    key: 'organisation_id',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.organisation`,
      defaultMessage: 'Organisation',
      description: 'The column name for column organisation',
    }),
    isSortable: false,
    type: 'organisation',
  },
  {
    key: 'client_id',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.client`,
      defaultMessage: 'Client',
      description: 'The column name for column client',
    }),
    isSortable: false,
    type: 'client',
  },
  {
    key: 'created_at',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.createdAt`,
      defaultMessage: 'Created at',
      description: 'The column name for column created at',
    }),
    type: 'datetime',
  },
  {
    key: 'is_confirmed',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.isConfirmed`,
      defaultMessage: 'Confirmed',
      description: 'The column name for column is confirmed',
    }),
    isSortable: false,
  },
  {
    key: 'is_accepted',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.isAccepted`,
      defaultMessage: 'Accepted',
      description: 'The column name for column is accepted',
    }),
    isSortable: false,
  },
  {
    key: 'is_finished',
    name: intl.formatMessage({
      id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.isFinished`,
      defaultMessage: 'Finished',
      description: 'The column name for column is finished',
    }),
    isSortable: false,
  },
];

const OrdersGrid = ({ defaultProperties, defaultView, fixedFilters, modules, onActiveItem }) => {
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const searchIdentifier = {
    code: ORDERS_PROFILE_CODE,
    model: ORDERS_PROFILE_MODEL,
    namespace: ORDERS_PROFILE_NAMESPACE,
  };
  const fixedProperties = [
    {
      key: 'number',
      name: intl.formatMessage({
        id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.number`,
        defaultMessage: 'Order number',
        description: 'The column name for column number',
      }),
      type: 'order',
      isSortable: true,
    },
  ];
  const properties = defaultProperties ?? getDefaultOrderProperties(intl);
  const { isFetchingProfile, filters, views, properties: filterProperties } = useDataSourceProfile({
    ...searchIdentifier,
    filterTypes: {
      client_id: 'client',
      contact_id: 'contact',
      order_tags: 'orderTags',
      organisation_id: 'organisation',
      sku: 'sku',
      total: 'currency',
      delivery_at: 'date',
      due_at: 'date',
      type: 'orderType',
      progress_allocation: 'orderProgressStatus',
      progress_capture: 'orderProgressStatus',
      progress_fulfilment: 'orderProgressStatus',
    },
    ignoreProperties: [
      'order_number',
      'tags',
      ...fixedProperties.map(property => property.key),
      ...properties.map(property => property.key),
    ],
    propertyTypes: {
      type: 'orderType',
      created_at: 'date',
      due_at: 'date',
      delivery_at: 'date',
      total: 'currency',
      contact_id: 'contact',
      organisation_id: 'organisation',
      client_id: 'client',
      progress_allocation: 'orderProgressStatus',
      progress_capture: 'orderProgressStatus',
      progress_fulfilment: 'orderProgressStatus',
      order_tags: 'orderTags',
    },
  });

  const dataSource = useDataGrid(searchQuery(searchIdentifier), {
    gridOptions: {
      columnSelection: true,
      copyGridUrl: true,
      filterSelection: true,
      advancedSort: true,
      viewsSelection: true,
    },
    options: {
      dataSourceUrl: true,
      isLoadingData: isFetchingProfile,
      storeStateKey: 'orders',
    },
    modules,
    dataProperties: {
      properties: [
        ...properties,
        ...filterProperties,
        ...fixedProperties,
        {
          key: 'reference',
          name: intl.formatMessage({
            id: `dataGrid.columns.${ORDERS_PROFILE_MODEL}.reference`,
            defaultMessage: 'Reference',
            description: 'The column name for column reference',
          }),
          isSortable: false,
        },
      ],
      fixedProperties,
    },
    views: {
      canCreateViewForTenant: sessionManager?.hasPermissions(['odr:orders:write']) ?? false,
      defaultView:
        defaultView ??
        createDefaultView(intl, {
          properties: [...properties],
          sort: [
            {
              key: 'created_at',
              direction: 'desc',
            },
          ],
        }),
      views,
      viewShortcuts: true,
      onAddView: (view, otherViews) =>
        createDataGridProfileView(ORDERS_PROFILE_PATH)(view, { views: otherViews }),
      onUpdateView: view => updateDataGridProfileView(ORDERS_PROFILE_PATH)(view),
      onDeleteView: view => updateDataGridProfileView(ORDERS_PROFILE_PATH)(view),
      onRepositionView: repositionedViews =>
        updateDataGridProfilePositions(ORDERS_PROFILE_PATH)(repositionedViews),
      onChangeDefaultView: updatedViews =>
        updateDataGridDefaultView(ORDERS_PROFILE_PATH)(updatedViews),
    },
    filters: {
      loadFilterOptions: filterOptionsQuery(searchIdentifier),
      fixedFilters,
      filters,
    },
  });
  const { activeItem: activeItemModule = {} } = dataSource;
  const { activeItem } = activeItemModule;

  useEffect(() => {
    if (onActiveItem) {
      onActiveItem(activeItem);
    }
  }, [activeItem, onActiveItem]);

  const renderOrder = orderData => <OrderCard order={orderData} key={orderData?.id} />;
  const renderOrderSkeleton = key => <OrderCardSkeleton key={key} />;

  const filterFormatters = {
    orderType: OrderTypeFilter,
    orderProgressStatus: OrderProgressStatusFilter,
    orderTags: OrderTagsFilter,
  };

  const filterRenderers = {
    orderType: OrderTypeRenderer,
    orderProgressStatus: OrderProgressStatusRenderer,
    orderTags: OrderTagsRenderer,
  };

  return (
    <>
      <Hidden mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderOrder}
          renderSkeletonItem={renderOrderSkeleton}
          component={OrdersContainer}
          filterFormatters={filterFormatters}
          filterRenderers={filterRenderers}
        />
      </Hidden>
      <Hidden smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{
            order: orderFormatter,
            client: clientFormatter,
            contact: contactFormatter,
            organisation: organisationFormatter,
            orderType: orderTypeFormatter,
            orderProgressStatus: orderProgressStatusFormatter,
            orderTags: orderTagsFormatter,
          }}
          filterFormatters={filterFormatters}
          filterRenderers={filterRenderers}
        />
      </Hidden>
    </>
  );
};

OrdersGrid.propTypes = {
  defaultProperties: PropTypes.array,
  defaultView: PropTypes.object,
  fixedFilters: PropTypes.array,
  modules: PropTypes.array,
  onActiveItem: PropTypes.func,
};

OrdersGrid.defaultProps = {
  defaultProperties: undefined,
  defaultView: undefined,
  fixedFilters: [],
  modules: ['dataProperties', 'pagination', 'activeItem', 'filters', 'sorting', 'views', 'search'],
  onActiveItem: undefined,
};

export default OrdersGrid;
